import { Component, OnInit, Input, Output, EventEmitter, OnChanges, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, FormControl, Validators, AbstractControl } from '@angular/forms';

import { DatePipe } from '@angular/common';
import { SearchService } from '@app/data/services/search.service';
import { MultipleImageUploaderComponent } from '@app/shared/components/multiple-image-uploader/multiple-image-uploader.component';
import { ErrorScrollService } from '@app/core/services/errorScroll.service';
import { Tag, TagItem, SearchItem } from '@app/data/models';

@Component({
    selector: 'app-article-form',
    templateUrl: './article-form.component.html',
    styleUrls: ['./article-form.component.scss'],
    providers: [DatePipe, ErrorScrollService],
    standalone: false,
})
export class ArticleFormComponent implements OnInit, OnChanges {
    @Input() fields;
    @Input() type;
    @Input() showTags;
    @Input() showLinks;
    @Input() isSubmitting;
    @Input() formErrors;
    @Input() post;
    @Input() edit: boolean;

    @Output() formSubmit = new EventEmitter();
    @Output() formClose = new EventEmitter();
    @ViewChild('imageUpload', { static: false }) imageUpload: MultipleImageUploaderComponent;

    failureMessage: string;
    addArticleForm: FormGroup;
    errorMessage: string;
    selectedTags = [];
    isImageChanged = false;
    formValidationErrors;
    formErrorMessage;
    postImages = [];
    screenshot = '';
    articleImage: null;
    articleType = null;

    constructor(
        private formBuilder: FormBuilder,
        private searchService: SearchService,
        private datePipe: DatePipe,
        public el: ElementRef,
        private errorScrollService: ErrorScrollService,
    ) {}

    ngOnInit() {
        this.createForm();

        if (this.post && this.post.images) {
            this.postImages = this.post.images;
        }
        if (this.post?.tags) {
            this.selectedTags = [...this.post.tags];
        }
    }

    ngOnChanges(changes) {
        if (changes.formErrors && changes.formErrors.currentValue) {
            this.formValidationErrors = this.formErrors;
            this.setFormFieldsErrors(this.formValidationErrors);
            this.errorMessage = 'There was an error adding the post.';

            document.querySelector('.content').scrollTop = 0;

            setTimeout(() => {
                this.errorScrollService.scrollToFirstInvalidControl(this.addArticleForm, this.el.nativeElement);
            }, 0);
        }
    }

    setImageSrc(imageSrc) {
        this.articleImage = imageSrc;
        this.isImageChanged = true;
    }

    toFormGroup(fields) {
        const group: Record<string, AbstractControl> = {};

        fields.forEach((field) => {
            const validators = [];
            if (field.required) {
                validators.push(Validators.required);
            }

            // TODO: remove it after backend is fixed
            if (field.name === 'content' && this.post) {
                group[field.name] = new FormControl(
                    this.post.data[field.name] || this.post.data['body'] || this.post.data['full_description'],
                    validators,
                );
            } else if (field.name === 'publish_date' && this.post) {
                const date = this.post.data[field.name] ? new Date(this.post.data[field.name]) : new Date();
                group[field.name] = new FormControl(this.datePipe.transform(date, 'yyyy-MM-dd'), validators);
            } else if (field.name === 'price' && this.post) {
                group[field.name] = new FormControl(this.post.data['price'] || this.post.data['sold_by'], validators);
            } else {
                const postFieldName = this.type === 'whois' ? this.alignWhoisFieldName(field.name) : field.name;
                group[field.name] = new FormControl(
                    this.post
                        ? this.post.data[postFieldName]
                        : field.type == 'number' || field.type == 'radio-button'
                          ? null
                          : '',
                    validators,
                );
            }

            if (field.type == 'host-names-field') {
                group[field.name] = this.formBuilder.array([]);
            }
        });

        return new FormGroup(group);
    }

    onChange(fieldName) {
        if (this.formValidationErrors?.[fieldName]) {
            this.formValidationErrors[fieldName] = null;
        }

        this.errorMessage = null;
    }

    onHostNameChanges(index, field, fieldName) {
        if (this.formValidationErrors?.[field]?.[index]?.[fieldName]) {
            this.formValidationErrors[field][index][fieldName] = null;
        }

        this.errorMessage = null;
    }

    isAddedItem(tagId: number, selectedItems: Tag[]) {
        return selectedItems.some((item) => item.id == tagId);
    }

    onToggleItem(item) {
        this.errorMessage = null;
        this.processItem(item);
    }

    toggleItems(item: TagItem[] | SearchItem[]) {
        this.errorMessage = null;
        item.forEach((i) => this.processItem(i));
    }

    processItem(item) {
        if (!this.isAddedItem(item.id, this.selectedTags)) {
            this.selectedTags.push(item);
        } else {
            const itemIndex = this.selectedTags.indexOf(item, 0);
            if (itemIndex > -1) {
                this.selectedTags.splice(itemIndex, 1);
            }
        }
    }

    onSelectItems(tags) {
        tags.forEach((item) => {
            if (!this.isAddedItem(item.id, this.selectedTags)) {
                this.selectedTags.push(item);
            }
        });
    }

    onUnSelectItems(tags) {
        tags.forEach((item) => {
            if (this.isAddedItem(item.id, this.selectedTags)) {
                const itemIndex = this.selectedTags.indexOf(item, 0);
                if (itemIndex > -1) {
                    this.selectedTags.splice(itemIndex, 1);
                }
            }
        });
    }

    deleteHostName(index, fieldName) {
        this[fieldName] = this.addArticleForm.controls[fieldName] as FormArray;
        this[fieldName].removeAt(index);
    }

    addHostName(fieldName): void {
        this[fieldName] = this.addArticleForm.controls[fieldName] as FormArray;
        this[fieldName].push(this.createHostName());
    }

    setScreenshotSrc(imageSrc) {
        this.screenshot = imageSrc;
    }

    onSubmit() {
        const formData = this.addArticleForm.value;

        if (this.showTags) {
            formData.tags = this.selectedTags.map((t) => t.id.toString());
        } else {
            delete formData.tags;
        }

        if (this.imageUpload) {
            formData.images = this.imageUpload.urls && this.imageUpload.urls.length ? this.imageUpload.urls : [];
        }

        if (this.type === 'whois') {
            formData.screenshot = this.screenshot ?? '';
        }

        formData.is_selected = this.post?.data.is_selected || false;

        this.formSubmit.emit(formData);
    }

    close() {
        this.formClose.emit();
    }

    private alignWhoisFieldName(fieldName: string): string {
        if (fieldName.startsWith('record_')) {
            return fieldName.replace(/_/g, '.');
        } else {
            return fieldName;
        }
    }

    private createHostName(): FormGroup {
        return this.formBuilder.group({
            numeric: '',
            str: '',
        });
    }

    private createForm() {
        this.addArticleForm = this.toFormGroup(this.fields);
    }

    private setFormFieldsErrors(errors) {
        for (const errorFieldName in errors) {
            const field = this.addArticleForm.get(errorFieldName);
            if (field) {
                field.setErrors({ serverErrors: errors[errorFieldName] });
            }
        }
    }
}
